<template>
  <ais-state-results>
    <template #default="{ state: { query } }">
      <script v-html="jsonld" type="application/ld+json"></script>
      <div class="relative" ref="breadCrumb">
        <div
          class="gradient-left absolute h-full w-76px pointer-events-none z-20 bg-gradient-to-r from-linear-3 to-linear-4"
          v-show="breadcrumbIsScrolledLeft"
        />
        <vs-breadcrumb>
          <li
            v-for="(breadcrumb, index) in getUpdatedBreadcrumbs(query)"
            :class="index === breadcrumbs.length - 1 ? 'vs-active' : ''"
            :key="breadcrumb"
            class="vs-listitem"
            @click="onClick(index)"
          >
            <a
              :title="breadcrumb"
              @click.prevent
              :href="`${createURL(getBreadcrumbQuery(index))}`"
              class="vs-breadcrumb--link"
            >{{ breadcrumb }}</a
            >
            <span
              class="vs-breadcrum--separator"
              v-if="breadcrumb && index !== breadcrumbs.length - 1"
            >/</span
            >
          </li>
        </vs-breadcrumb>
        <div
          class="gradient-right absolute h-full w-76px pointer-events-none z-20 bg-gradient-to-r from-linear-1 to-linear-5"
          v-show="breadcrumbIsScrolledRight"
        />
      </div>
      <div class="mx-4 lg:mx-0" v-if="breadcrumbs.length > 1">
        <h1 class="text-sm text-chrome">
          {{params['product_brand'] ?? 'Driver'}} Jobs
          {{
            breadcrumbs.length === 2
              ? breadcrumbs[1]
              : `near ${breadcrumbs[3] ? `${breadcrumbs[3]}, ` : ''}${
                breadcrumbs[2]
              }${breadcrumbs[1] ? `, ${breadcrumbs[1]}` : ''}`
          }}
        </h1>
      </div>
    </template>
  </ais-state-results>
</template>

<script>
import {
  getUrlParameters,
  getBreadcrumbs,
  getCityStateUsingSplitQuery,
  isZipCodeSearch,
} from '../utils';

export default {
  name: 'JobsBreadcrumbs',
  props: {
    paramProp: {
      default: undefined,
    },
  },
  data() {
    return {
      params : this.paramProp ? JSON.parse(this.paramProp) : {},
      breadcrumbs: [],
      breadcrumbIsScrolledLeft: false,
      breadcrumbIsScrolledRight: false,
      page: '',
      vsBreadcrumb: null,
      vsBreadcrumbOl: null,
    };
  },
  computed: {
    jsonld() {
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: this.breadcrumbs.map((breadcrumb, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            name: breadcrumb,
            item: this.createURL(this.getBreadcrumbQuery(index)),
          };
        }),
      };
    },
  },
  methods: {
    createURL(query) {
      const urlParameters = getUrlParameters();
      const { baseUrl, hash } = urlParameters;
      let page = urlParameters.page;
      this.page = page;
      const queryParameters = new URL(location).searchParams;
      let pathParameters = [];
      let queryString = '';

      //Update Canonical Url
      this.updateCanonicalUrl(query, baseUrl);

      if (page === 'locations') {
        pathParameters = this.getPathParametersUsingQuery(query);
        if (this.breadcrumbs.length === 4) {
          page = 'jobs';
          if (isZipCodeSearch(query)) {
            pathParameters = [];
            queryParameters.set('query', encodeURIComponent(query));
            queryString = query ? `?${queryParameters.toString()}` : '';
          }
        }
      } else {
        queryParameters.set('query', encodeURIComponent(query));
        queryString = query ? `?${queryParameters.toString()}` : '';
      }

      return `${baseUrl}${page}/${pathParameters.join(
        '/',
      )}${queryString}${hash}`;
    },
    getUpdatedBreadcrumbs(query) {
      getBreadcrumbs(query).then(breadcrumbs => {
        if (String(breadcrumbs) !== String(this.breadcrumbs)) {
          this.breadcrumbs = breadcrumbs;
        }
      });
      return this.breadcrumbs;
    },
    updateCanonicalUrl(query, baseUrl) {
      let pathParameters = this.getPathParametersUsingQuery(query);
      const page = 'locations';
      const newUrl = `${baseUrl}${page}/${pathParameters.join('/')}`;
      const canonicalLink = document.querySelector("link[rel='canonical']");
      if (canonicalLink) {
        canonicalLink.href = newUrl;
      }
    },
    getPathParametersUsingQuery(query) {
      const queryParams = getCityStateUsingSplitQuery(query);
      let pathParameters = [];
      if (queryParams && queryParams['state']) {
        pathParameters.push(queryParams['state']);
      }
      if (queryParams && queryParams['city']) {
        pathParameters.push(queryParams['city']);
      }
      return pathParameters;
    },
    getBreadcrumbQuery(index) {
      switch (index) {
        case 1:
        case 3:
          return this.breadcrumbs[index] ?? '';
        case 2:
          return `${this.breadcrumbs[index]}, ${this.breadcrumbs[index - 1]}`;
        default:
          return '';
      }
    },
    onClick(index) {
      const breadcrumbQuery = this.getBreadcrumbQuery(index);
      if (index !== this.breadcrumbs.length - 1) {
        if (this.page === 'locations') {
          window.location = this.createURL(breadcrumbQuery);
        } else {
          this.$root.$emit('breadcrumbStateChange', breadcrumbQuery);
        }
      }
    },
    breadcrumbScrolled() {
      const navContent = this.vsBreadcrumbOl.getBoundingClientRect();
      const navWrapper = this.vsBreadcrumb.getBoundingClientRect();
      const xFinal = Math.ceil(
        navWrapper.left - (navContent.width - navWrapper.width),
      );
      this.breadcrumbIsScrolledLeft = navContent.x < navWrapper.x;
      this.breadcrumbIsScrolledRight = navContent.x > xFinal;
    },
  },
  watch: {
    breadcrumbs() {
      if (
        this.$refs.breadCrumb &&
        (this.vsBreadcrumb === null || this.vsBreadcrumbOl === null)
      ) {
        this.vsBreadcrumb =
          this.$refs.breadCrumb.querySelector('nav.vs-breadcrumb');
        this.vsBreadcrumbOl = this.$refs.breadCrumb.querySelector(
          'ol.vs-breadcrumb--ol',
        );
      }

      if (this.vsBreadcrumb && this.vsBreadcrumbOl) {
        this.vsBreadcrumb.addEventListener('scroll', this.breadcrumbScrolled);
        this.breadcrumbScrolled();
      }
    },
  },
};
</script>
<style lang="scss">
.gradient-left {
  background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0) 100%
  );
}

.gradient-right {
  background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 100%
  );
}

.vs-breadcrumb {
  width: 100%;
  overflow: auto !important;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  .vs-breadcrumb--ol {
    display: block;

    white-space: nowrap !important;

    @media screen and (min-width: 1024px) {
      padding-left: 0 !important;
    }

    li {
      display: inline-block !important;
    }

    li.vs-listitem,
    .disabled-link {
      &:hover {
        a.vs-breadcrumb--link {
          font-weight: 400 !important;
        }
      }

      a.vs-breadcrumb--link {
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: normal !important;
        color: #85858b !important;
        &:hover {
          text-decoration: underline;
        }
      }
    }

    li:not(.disabled-link) {
      .vs-breadcrumb--link {
        cursor: pointer !important;
      }
    }

    li.vs-active {
      &:hover {
        a.vs-breadcrumb--link {
          font-weight: 400 !important;
        }
      }

      a.vs-breadcrumb--link {
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: normal !important;
        color: var(--primary-breadcrumb) !important;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
